import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { X, Phone, Loader2, Users as UsersIcon } from "lucide-react";

// WhatsApp-brand green used by the official button
const WHATSAPP_GREEN = "#25D366";

/**
 * Builds a pretty, human-readable WhatsApp message with the latest Health
 * Score + vitals + top AI recommendations so the patient can share it with
 * their doctor / family in one tap.
 */
function buildWhatsAppMessage({ userName, vitals, aiReportText }) {
    const greet = `Hi! Here's my latest Medx AI valuation${userName ? ` for ${userName}` : ""} 💊`;
    const score = vitals?.health_score != null
        ? `\n\n*Health Score: ${vitals.health_score}/100*${vitals.triage_flag ? ` · ${vitals.triage_flag}` : ""}`
        : "";
    const whenISO = vitals?.captured_at || new Date().toISOString();
    const when = `\n_Scanned ${new Date(whenISO).toLocaleString()}_`;
    const lines = [];
    if (vitals?.blood_pressure_systolic) lines.push(`• Blood Pressure: ${vitals.blood_pressure_systolic}/${vitals.blood_pressure_diastolic} mmHg`);
    if (vitals?.heart_rate) lines.push(`• Heart Rate: ${vitals.heart_rate} bpm`);
    if (vitals?.spo2) lines.push(`• SpO₂: ${vitals.spo2}%`);
    if (vitals?.body_temperature) lines.push(`• Body Temp: ${vitals.body_temperature} °C`);
    if (vitals?.bmi) lines.push(`• BMI: ${vitals.bmi}`);
    if (vitals?.respiratory_rate) lines.push(`• Respiratory Rate: ${vitals.respiratory_rate} /min`);
    if (vitals?.blood_glucose) lines.push(`• Blood Glucose: ${vitals.blood_glucose} mg/dL`);
    const vitalsBlock = lines.length ? `\n\n*Latest Vitals*\n${lines.join("\n")}` : "";

    let aiBlock = "";
    if (aiReportText) {
        // Trim to the first ~500 chars so it fits in a WhatsApp preview.
        const compact = aiReportText.replace(/\n{3,}/g, "\n\n").trim().slice(0, 500);
        aiBlock = `\n\n*AI Summary*\n${compact}${aiReportText.length > 500 ? "…" : ""}`;
    }

    return `${greet}${score}${when}${vitalsBlock}${aiBlock}\n\n— Sent from my Medx AI dashboard`;
}

/**
 * Compact "Share on WhatsApp" button. On tap, opens wa.me with the valuation
 * pre-filled. No API, no credentials needed — uses the device's own WhatsApp.
 * If the patient has a saved mobile number we pre-fill the recipient to
 * themselves, otherwise they pick the recipient inside WhatsApp.
 */
export function WhatsAppShareButton({ user, vitals, className = "", label = "Share on WhatsApp" }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const openWhatsApp = (toNumber) => {
        setBusy(true);
        // Fetch the latest AI report (if any) so we can include a short summary.
        api
            .get("/ai/valuation-reports")
            .then(({ data }) => (Array.isArray(data) && data.length ? (data[0].content || "") : ""))
            .catch(() => "")
            .then((aiReportText) => {
                const msg = buildWhatsAppMessage({
                    userName: user?.first_name || user?.name || "",
                    vitals,
                    aiReportText,
                });
                const encoded = encodeURIComponent(msg);
                const digits = (toNumber || "").replace(/\D/g, "");
                const url = digits
                    ? `https://wa.me/${digits}?text=${encoded}`
                    : `https://wa.me/?text=${encoded}`;
                window.open(url, "_blank", "noopener,noreferrer");
                toast.success("Opening WhatsApp…");
                setOpen(false);
            })
            .finally(() => setBusy(false));
    };

    if (!vitals) {
        return null;
    }

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold text-white shadow-sm hover:brightness-105 ${className}`}
                style={{ background: WHATSAPP_GREEN }}
                data-testid="whatsapp-share-btn"
            >
                <WhatsAppIcon className="w-4 h-4" /> {label}
            </button>
            {open && (
                <ChoiceModal
                    busy={busy}
                    user={user}
                    onCancel={() => setOpen(false)}
                    onPickSelf={() => openWhatsApp(user?.mobile ? `${user.country_code || ""}${user.mobile}` : null)}
                    onPickShare={() => openWhatsApp(null)}
                    onPickOther={(num) => openWhatsApp(num)}
                />
            )}
        </>
    );
}

function ChoiceModal({ user, busy, onCancel, onPickSelf, onPickShare, onPickOther, title = "Send your current valuation", subtitle = "We'll open WhatsApp with your latest Health Score, vitals and AI summary already typed. You just tap send." }) {
    const [showManual, setShowManual] = useState(false);
    const [manual, setManual] = useState("");
    const hasOwn = !!user?.mobile;
    const submitManual = (e) => {
        e.preventDefault();
        if (!manual.trim()) return;
        onPickOther(manual.trim());
    };
    return (
        <div
            className="fixed inset-0 z-[70] grid place-items-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={onCancel}
            data-testid="whatsapp-choice-modal"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
                style={{ maxHeight: "90vh" }}
            >
                <div className="relative p-5 border-b border-gray-100 flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl grid place-items-center text-white" style={{ background: WHATSAPP_GREEN }}>
                            <WhatsAppIcon className="w-5 h-5" />
                        </div>
                        <div>
                            <div className="text-[10px] font-mono tracking-[0.22em] text-emerald-700">SHARE ON WHATSAPP</div>
                            <h3 className="font-display text-lg font-semibold">{title}</h3>
                        </div>
                    </div>
                    <button onClick={onCancel} className="w-9 h-9 rounded-full hover:bg-gray-100 grid place-items-center text-gray-500 shrink-0" aria-label="Close" data-testid="whatsapp-choice-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-3">
                    <p className="text-sm text-gray-600">
                        {subtitle}
                    </p>
                    {hasOwn && (
                        <button
                            disabled={busy}
                            onClick={onPickSelf}
                            className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 text-left disabled:opacity-60"
                            data-testid="whatsapp-send-self"
                        >
                            <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center">
                                <Phone className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm">Send to myself</div>
                                <div className="text-xs text-gray-500 truncate">{user.country_code} {user.mobile}</div>
                            </div>
                        </button>
                    )}
                    <button
                        disabled={busy}
                        onClick={onPickShare}
                        className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 text-left disabled:opacity-60"
                        data-testid="whatsapp-send-pick"
                    >
                        <div className="w-9 h-9 rounded-full grid place-items-center text-white" style={{ background: WHATSAPP_GREEN }}>
                            <WhatsAppIcon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-semibold text-sm">Pick a contact in WhatsApp</div>
                            <div className="text-xs text-gray-500">Share with a family member, doctor or caregiver.</div>
                        </div>
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowManual((v) => !v)}
                        className="text-xs font-semibold text-emerald-700 hover:underline"
                        data-testid="whatsapp-send-manual-toggle"
                    >
                        {showManual ? "Hide" : "Or type a number"}
                    </button>
                    {showManual && (
                        <form onSubmit={submitManual} className="flex items-center gap-2">
                            <input
                                value={manual}
                                onChange={(e) => setManual(e.target.value)}
                                placeholder="+27 82 123 4567"
                                className="flex-1 px-3 py-2 rounded-xl border border-gray-200 focus:border-emerald-400 outline-none text-sm"
                                data-testid="whatsapp-manual-input"
                            />
                            <button
                                type="submit"
                                disabled={busy}
                                className="px-4 py-2 rounded-xl text-white text-sm font-semibold disabled:opacity-60"
                                style={{ background: WHATSAPP_GREEN }}
                                data-testid="whatsapp-manual-submit"
                            >
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Open"}
                            </button>
                        </form>
                    )}
                    <div className="text-[11px] text-gray-400 leading-relaxed pt-2 border-t border-gray-100">
                        Tip: nothing is sent until you tap the send button inside WhatsApp. Your data never passes through third-party servers.
                    </div>
                </div>
            </div>
        </div>
    );
}

function WhatsAppIcon({ className = "w-5 h-5" }) {
    return (
        <svg className={className} viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
            <path d="M16 2C8.3 2 2 8.3 2 16c0 2.6.7 5.1 2 7.3L2 30l6.9-1.9C11.1 29.4 13.5 30 16 30c7.7 0 14-6.3 14-14S23.7 2 16 2zm0 25.4c-2.2 0-4.3-.6-6.2-1.8l-.4-.2-4.1 1.1 1.1-4-.3-.4C4.8 20.2 4 18.1 4 16 4 9.4 9.4 4 16 4s12 5.4 12 12-5.4 11.4-12 11.4zm6.3-8.3c-.3-.2-2-.9-2.3-1-.3-.1-.5-.2-.7.2-.2.3-.8 1-1 1.2-.2.2-.4.2-.7.1-.3-.2-1.3-.5-2.5-1.6-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5s-.7-1.7-1-2.4c-.3-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1.1 1.1-1.1 2.6s1.1 3 1.3 3.3c.2.2 2.2 3.4 5.4 4.8 2.6 1.2 3.3 1 3.9.9.6-.1 2-.8 2.3-1.6.3-.8.3-1.5.2-1.6-.2-.1-.4-.2-.7-.4z"/>
        </svg>
    );
}


function scoreTone(score) {
    if (score == null || score === 0) return "—";
    if (score >= 80) return "💚 Great";
    if (score >= 65) return "💛 Good";
    if (score >= 45) return "🟠 Fair";
    return "🔴 Needs attention";
}

/**
 * Builds a one-tap "Family Health Snapshot" WhatsApp message that lists every
 * approved dependent + the account owner with their latest Health Score and
 * vitals in a single consolidated card.
 */
async function buildFamilyMessage({ parentUser, parentVitals, dependents }) {
    const date = new Date().toLocaleDateString();
    const lines = [`*Medx AI — Family Health Snapshot*`, `_${date}_`, ""];

    const push = (label, score, vitals) => {
        const tone = scoreTone(score);
        lines.push(`*${label}* — Health Score: ${score || 0}/100 · ${tone}`);
        if (vitals) {
            const bits = [];
            if (vitals.blood_pressure_systolic) bits.push(`BP ${vitals.blood_pressure_systolic}/${vitals.blood_pressure_diastolic}`);
            if (vitals.heart_rate) bits.push(`HR ${vitals.heart_rate} bpm`);
            if (vitals.spo2) bits.push(`SpO₂ ${vitals.spo2}%`);
            if (vitals.body_temperature) bits.push(`${vitals.body_temperature}°C`);
            if (vitals.bmi) bits.push(`BMI ${vitals.bmi}`);
            if (bits.length) lines.push(`  ${bits.join(" · ")}`);
            if (vitals.captured_at) lines.push(`  _Scanned ${new Date(vitals.captured_at).toLocaleDateString()}_`);
        } else {
            lines.push(`  _No scan yet — walk up to a Medx AI kiosk to begin._`);
        }
        lines.push("");
    };

    // Parent first — fetch latest parent vitals explicitly so this works
    // even when the active profile is currently a dependent.
    let selfVitals = parentVitals;
    try {
        const { data } = await api.get("/me/dashboard");
        selfVitals = data?.latest_vitals || parentVitals;
    } catch { /* fall back to the vitals we were handed */ }
    push(`${parentUser?.first_name || parentUser?.name || "Me"} (account owner)`, parentUser?.health_score, selfVitals);

    // Dependents — fetch each dependent's latest vitals in parallel
    const approved = (dependents || []).filter((d) => d.status === "approved");
    const depDashboards = await Promise.all(
        approved.map((d) =>
            api.get(`/me/dashboard?dependent_id=${d.id}`).then(({ data }) => data).catch(() => null)
        )
    );
    approved.forEach((d, i) => {
        const dash = depDashboards[i];
        const label = `${d.name} (${d.relationship}, age ${d.age})`;
        push(label, d.health_score, dash?.latest_vitals);
    });

    lines.push(`— Sent from Medx AI Family Dashboard`);
    return lines.join("\n");
}

/**
 * Button variant used in the Family tab + header "Send family report". One
 * tap opens WhatsApp pre-filled with every family member's current snapshot.
 */
export function WhatsAppFamilyButton({ parentUser, parentVitals, dependents, className = "", label = "WhatsApp family report" }) {
    const [busy, setBusy] = useState(false);
    const [open, setOpen] = useState(false);

    const openWhatsApp = async (toNumber) => {
        setBusy(true);
        try {
            const msg = await buildFamilyMessage({ parentUser, parentVitals, dependents });
            const encoded = encodeURIComponent(msg);
            const digits = (toNumber || "").replace(/\D/g, "");
            const url = digits ? `https://wa.me/${digits}?text=${encoded}` : `https://wa.me/?text=${encoded}`;
            window.open(url, "_blank", "noopener,noreferrer");
            toast.success("Opening WhatsApp with the family snapshot…");
            setOpen(false);
        } catch {
            toast.error("Could not build the family report. Please try again.");
        } finally { setBusy(false); }
    };

    const approvedCount = (dependents || []).filter((d) => d.status === "approved").length;
    if (approvedCount === 0) return null; // nothing to share

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold text-white shadow-sm hover:brightness-105 ${className}`}
                style={{ background: WHATSAPP_GREEN }}
                data-testid="whatsapp-family-btn"
            >
                <UsersIcon className="w-4 h-4" /> {label}
            </button>
            {open && (
                <ChoiceModal
                    busy={busy}
                    user={parentUser}
                    title="Share family health snapshot"
                    subtitle={`Includes you + ${approvedCount} dependent${approvedCount > 1 ? "s" : ""} with the latest Health Score & vitals.`}
                    onCancel={() => setOpen(false)}
                    onPickSelf={() => openWhatsApp(parentUser?.mobile ? `${parentUser.country_code || ""}${parentUser.mobile}` : null)}
                    onPickShare={() => openWhatsApp(null)}
                    onPickOther={(num) => openWhatsApp(num)}
                />
            )}
        </>
    );
}

